import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  cn,
} from '@command-center/ui-system';

/**
 * The detail drawer for a log row.
 *
 * Built on the shared `Sheet` rather than imported from a portal so the module
 * carries no app dependency. It portals to the document body, which is what
 * keeps a long metadata payload scrollable inside the drawer instead of
 * stretching the page behind it.
 */
export function LogDrawer({
  title,
  description,
  icon: Icon,
  onClose,
  children,
  widthClassName,
}: {
  title: ReactNode;
  description?: ReactNode;
  icon?: LucideIcon;
  onClose: () => void;
  children: ReactNode;
  widthClassName?: string;
}) {
  return (
    <Sheet open onOpenChange={(next) => !next && onClose()}>
      <SheetContent
        side="right"
        className={cn('w-full sm:max-w-[680px] md:max-w-[760px]', widthClassName)}
      >
        <SheetHeader>
          <div className="flex items-start gap-3 pr-8">
            {Icon && (
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-emphasis text-emphasis-foreground">
                <Icon className="h-5 w-5" aria-hidden />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <SheetTitle className="break-words">{title}</SheetTitle>
              {description && (
                <SheetDescription asChild>
                  <div className="mt-1 flex flex-wrap items-center gap-2">{description}</div>
                </SheetDescription>
              )}
            </div>
          </div>
        </SheetHeader>
        {children}
      </SheetContent>
    </Sheet>
  );
}

export function LogField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 break-words">{children}</div>
    </div>
  );
}

/**
 * A JSON payload in the drawer.
 *
 * Capped and independently scrollable: metadata blobs run to hundreds of lines
 * on integration rows, and an uncapped `<pre>` pushes every field below it off
 * the screen.
 */
export function LogJson({ label, value }: { label: string; value: unknown }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <pre className="mt-1 max-h-56 overflow-auto rounded-md bg-muted/50 p-2 text-[11px] leading-relaxed">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}
